import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api, formatErr } from "../../../../lib/api";
import { Button } from "../../../../components/ui/button";
import { Pencil } from "lucide-react";
import { toast } from "sonner";
import { DetailField, SectionCard, StatusPill } from "./shared";
import { ACCOUNTING_BASE } from "./accountingNav";

export default function VendorViewPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [vendor, setVendor] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get(`/society/accounting/engine/vendors/${id}`);
        setVendor(res.data);
      } catch (err) {
        toast.error(formatErr(err, "Could not load this vendor"));
        navigate(`${ACCOUNTING_BASE}/vendors`, { replace: true });
      } finally {
        setLoading(false);
      }
    })();
  }, [id, navigate]);

  if (loading) return <div className="text-sm text-[#6B7280]">Loading...</div>;
  if (!vendor) return null;

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <Link to={`${ACCOUNTING_BASE}/vendors`} className="text-xs font-semibold uppercase tracking-[0.16em] text-[#6B7280] hover:text-[#111827]">
            &larr; Vendors
          </Link>
          <h2 className="mt-1 text-lg font-extrabold text-[#111827]">{vendor.vendorName}</h2>
        </div>
        <Button onClick={() => navigate(`${ACCOUNTING_BASE}/vendors/${id}/edit`)} className="bg-[#111827] text-white hover:bg-[#374151]">
          <Pencil className="mr-2 h-4 w-4" /> Edit
        </Button>
      </div>

      <SectionCard title="Vendor details">
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <DetailField label="Contact person" value={vendor.contactPerson} />
          <DetailField label="Mobile" value={vendor.mobile} />
          <DetailField label="Email" value={vendor.email} />
          <DetailField label="GSTIN" value={vendor.gstin} />
          <DetailField label="PAN" value={vendor.pan} />
          <DetailField label="Payment terms" value={vendor.paymentTerms} />
          <DetailField label="Linked payable ledger" value={vendor.linkedLedgerName} />
          <DetailField label="Outstanding" value={vendor.outstanding === null || vendor.outstanding === undefined ? "Not available yet" : `₹ ${Number(vendor.outstanding).toLocaleString()}`} />
          <div>
            <div className="text-xs uppercase tracking-[0.14em] text-[#6B7280]">Status</div>
            <div className="mt-1"><StatusPill active={vendor.active !== false} /></div>
          </div>
        </div>
        {vendor.address && <div className="mt-4"><div className="text-xs uppercase tracking-[0.14em] text-[#6B7280]">Address</div><p className="mt-1 text-sm text-[#374151]">{vendor.address}</p></div>}
        {vendor.notes && <div className="mt-4"><div className="text-xs uppercase tracking-[0.14em] text-[#6B7280]">Notes</div><p className="mt-1 text-sm text-[#374151]">{vendor.notes}</p></div>}
      </SectionCard>

      <SectionCard title="Record history">
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <DetailField label="Created" value={vendor.createdAt ? new Date(vendor.createdAt).toLocaleString() : "-"} />
          <DetailField label="Created by" value={vendor.createdBy} />
          <DetailField label="Last updated" value={vendor.updatedAt ? new Date(vendor.updatedAt).toLocaleString() : "-"} />
          <DetailField label="Updated by" value={vendor.updatedBy || vendor.createdBy} />
        </div>
      </SectionCard>
    </div>
  );
}
